import React, { useState, useEffect, useCallback, useRef } from "react";
import { getTokenInternal } from "./useToken";
import Nav from "./Navigation";
import { useNavigate } from "react-router-dom";

const ManageItems = () => {
  const [items, setItems] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [mapsOptions, setMapsOptions] = useState([]);
  const [mapsNameOptions, setMapsNameOptions] = useState([]);
  const [gamesOptions, setGamesOptions] = useState([]);
  const [acquisitionMethods, setAcquisitionMethods] = useState([]);
  const [token, setToken] = useState(null);
  const [bulkEvent, setBulkEvent] = useState("");
  const [markNew, setMarkNew] = useState(false);
  const [bulkMessage, setBulkMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Edit modal state
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLinkedIds, setEditLinkedIds] = useState(new Set());
  const [editUnlockOptions, setEditUnlockOptions] = useState([]);
  const editOriginalIds = useRef(new Set());
  const [editSaved, setEditSaved] = useState(false);

  const navigate = useNavigate();

  const fetchItems = useCallback(async (tok) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items?active_only=false`,
        { headers: { Authorization: `Bearer ${tok.access_token}` } }
      );
      if (res.ok) setItems(await res.json());
    } catch (e) {
      console.error("Error fetching items:", e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const tok = await getTokenInternal();
      if (!tok) return;
      setToken(tok);

      if (!["owner", "management", "helper"].includes(tok.account.role)) {
        navigate("/");
        return;
      }

      fetchItems(tok);

      try {
        const [eventsRes, methodsRes, typesRes, mapsRes, mapsNameRes, gamesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/events`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/types`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/maps`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/maps_name`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/games`),
        ]);
        if (eventsRes.ok) setEventOptions(await eventsRes.json());
        if (methodsRes.ok) setAcquisitionMethods(await methodsRes.json());
        if (typesRes.ok) setTypeOptions(await typesRes.json());
        if (mapsRes.ok) setMapsOptions(await mapsRes.json());
        if (mapsNameRes.ok) setMapsNameOptions(await mapsNameRes.json());
        if (gamesRes.ok) setGamesOptions(await gamesRes.json());
      } catch (e) {
        console.error("Error fetching dropdown options:", e);
      }
    };
    init();
  }, [navigate, fetchItems]);

  const flash = (msg) => {
    setBulkMessage(msg);
    setTimeout(() => setBulkMessage(null), 4000);
  };

  const bulkAction = async (endpoint, extraParams = {}) => {
    try {
      const params = new URLSearchParams();
      if (bulkEvent) params.set("event", bulkEvent);
      Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));

      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}${endpoint}?${params}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        flash(`${data.updated} item(s) updated.`);
        fetchItems(token);
      }
    } catch (e) {
      console.error("Bulk action error:", e);
    }
  };

  const clearAllNew = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/clear-new`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        flash(`${data.updated} item(s) had their "New" badge cleared.`);
        fetchItems(token);
      }
    } catch (e) {
      console.error("Clear-new error:", e);
    }
  };

  const toggleFlag = async (item, field) => {
    try {
      const newFlags = {
        is_active: field === "is_active" ? !item.is_active : item.is_active,
        is_new: field === "is_new" ? !item.is_new : item.is_new,
        private_testing: field === "private_testing" ? !item.private_testing : item.private_testing,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/${item.id}/flags`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newFlags),
        }
      );
      if (res.ok) {
        const updated = await res.json();
        setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      }
    } catch (e) {
      console.error("Toggle flag error:", e);
    }
  };

  const updateAcquisitionMethod = async (item, methodId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/${item.id}/acquisition-method`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ acquisition_method_id: methodId || null }),
        }
      );
      if (res.ok) {
        const updated = await res.json();
        setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      }
    } catch (e) {
      console.error("Acquisition method update error:", e);
    }
  };

  // --- Edit modal helpers ---

  const openEdit = async (item) => {
    setEditSaved(false);
    setEditForm({
      name: item.name || "",
      types: item.types || "",
      event: item.event || "",
      maps: item.maps || "",
      maps_name: item.maps_name || "",
      url: item.url || "",
      games_id: item.games_id || "",
      acquisition_method_id: item.acquisition_method_id || "",
      is_active: item.is_active || false,
      is_new: item.is_new || false,
      private_testing: item.private_testing || false,
    });

    if (item.games_id) {
      const [reqOptions, byItem] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements?games_id=${item.games_id}`)
          .then(r => r.ok ? r.json() : []),
        fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/by-item?games_id=${item.games_id}`)
          .then(r => r.ok ? r.json() : {}),
      ]);
      setEditUnlockOptions(reqOptions);
      const ids = new Set((byItem[String(item.id)] || []).map(Number));
      editOriginalIds.current = new Set(ids);
      setEditLinkedIds(new Set(ids));
    } else {
      setEditUnlockOptions([]);
      editOriginalIds.current = new Set();
      setEditLinkedIds(new Set());
    }

    setEditingItem(item);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleEditRequirement = (reqId) => {
    setEditLinkedIds(prev => {
      const next = new Set(prev);
      if (next.has(reqId)) next.delete(reqId); else next.add(reqId);
      return next;
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...editForm,
      games_id: editForm.games_id ? parseInt(editForm.games_id) : null,
      acquisition_method_id: editForm.acquisition_method_id ? parseInt(editForm.acquisition_method_id) : null,
    };
    const res = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/items/${editingItem.id}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${token.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    if (res.ok) {
      const toLink = [...editLinkedIds].filter(id => !editOriginalIds.current.has(id));
      const toUnlink = [...editOriginalIds.current].filter(id => !editLinkedIds.has(id));
      await Promise.all([
        ...toLink.map(id => fetch(
          `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}/link-item/${editingItem.id}`,
          { method: "POST", headers: { Authorization: `Bearer ${token.access_token}` } }
        )),
        ...toUnlink.map(id => fetch(
          `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}/link-item/${editingItem.id}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
        )),
      ]);
      setEditSaved(true);
      fetchItems(token);
      setTimeout(() => setEditingItem(null), 1000);
    }
  };

  const handleEditDelete = async () => {
    if (!window.confirm(`Delete "${editingItem.name}"? This cannot be undone.`)) return;
    const res = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/items/${editingItem.id}`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
    );
    if (res.ok) {
      setEditingItem(null);
      fetchItems(token);
    }
  };

  const filtered = items.filter((item) => {
    const matchSearch =
      !search ||
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.event?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && item.is_active) ||
      (statusFilter === "hidden" && !item.is_active) ||
      (statusFilter === "new" && item.is_new) ||
      (statusFilter === "privateTesting" && item.private_testing);
    return matchSearch && matchStatus;
  });

  const counts = {
    all: items.length,
    active: items.filter((i) => i.is_active).length,
    hidden: items.filter((i) => !i.is_active).length,
    new: items.filter((i) => i.is_new).length,
    privateTesting: items.filter((i) => i.private_testing).length,
  };

  return (
    <div>
      <Nav />
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-white text-2xl font-bold mb-6">Manage Items</h1>

        {/* Bulk Actions */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <h2 className="text-white text-lg font-semibold mb-3">Bulk Actions</h2>

          <div className="mb-3">
            <label className="text-gray-300 text-sm block mb-1">
              Event scope (optional — leave blank to apply to all events):
            </label>
            <select
              value={bulkEvent}
              onChange={(e) => setBulkEvent(e.target.value)}
              className="p-2 rounded-md text-black text-sm w-64"
            >
              <option value="">All events</option>
              {eventOptions.map((e) => (
                <option key={e.id} value={e.name}>{e.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
                onClick={() => bulkAction("/api/items/activate-all", { mark_new: markNew })}
              >
                Activate All
              </button>
              <label className="flex items-center gap-1 text-gray-300 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={markNew}
                  onChange={(e) => setMarkNew(e.target.checked)}
                  className="w-4 h-4"
                />
                Also mark as New
              </label>
            </div>

            <button
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
              onClick={() => bulkAction("/api/items/deactivate-all")}
              title="Deactivates only items currently marked as New — safe undo for the last drop"
            >
              Deactivate New (Undo Drop)
            </button>

            <button
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md text-sm font-semibold"
              onClick={clearAllNew}
              title="Clears the New badge from all items without deactivating anything"
            >
              Clear All "New" Badges
            </button>

            <button
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-semibold"
              onClick={() => bulkAction("/api/items/make-public")}
              title="Moves every private-testing item to fully public — does not change is_active"
            >
              Make All Private-Testing Items Public
            </button>

            <button
              className="bg-purple-900 hover:bg-purple-950 text-white px-4 py-2 rounded-md text-sm font-semibold"
              onClick={() => bulkAction("/api/items/restrict-to-private-testing")}
              title="Marks every currently-public item as private-testing only — does not change is_active"
            >
              Restrict All to Private Testing
            </button>
          </div>

          {bulkMessage && (
            <p className="mt-3 text-green-400 text-sm font-semibold">{bulkMessage}</p>
          )}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <input
            type="text"
            placeholder="Search by name or event…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="p-2 rounded-md text-black text-sm w-56"
          />
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "hidden", label: "Hidden" },
            { key: "new", label: "New" },
            { key: "privateTesting", label: "Private Testing" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                statusFilter === key
                  ? "bg-white text-gray-900"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>

        {/* Item list */}
        <div className="space-y-2">
          {filtered.length === 0 && (
            <p className="text-gray-400 text-sm">No items match this filter.</p>
          )}
          {filtered.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 rounded-lg px-4 py-3 flex items-center justify-between gap-4"
            >
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold truncate">{item.name}</p>
                <p className="text-gray-400 text-xs">
                  {item.event || "—"} · {item.types || "—"}
                </p>
                {item.event === "Other" && (
                  <select
                    value={item.acquisition_method_id || ""}
                    onChange={(e) => updateAcquisitionMethod(item, e.target.value ? Number(e.target.value) : null)}
                    className="mt-1 p-0.5 rounded text-black text-xs w-48"
                  >
                    <option value="">— No method —</option>
                    {acquisitionMethods.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleFlag(item, "is_active")}
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    item.is_active
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-gray-600 hover:bg-gray-500 text-gray-200"
                  }`}
                  title={item.is_active ? "Click to hide" : "Click to activate"}
                >
                  {item.is_active ? "Active" : "Hidden"}
                </button>

                <button
                  onClick={() => toggleFlag(item, "is_new")}
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    item.is_new
                      ? "bg-blue-500 hover:bg-blue-600 text-white"
                      : "bg-gray-700 hover:bg-gray-600 text-gray-400"
                  }`}
                  title={item.is_new ? "Click to remove New badge" : "Click to mark as New"}
                >
                  {item.is_new ? "New" : "Not New"}
                </button>

                <button
                  onClick={() => toggleFlag(item, "private_testing")}
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    item.private_testing
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-gray-700 hover:bg-gray-600 text-gray-400"
                  }`}
                  title={item.private_testing ? "Click to make fully public" : "Click to restrict to private testers"}
                >
                  {item.private_testing ? "Private Testing" : "Public"}
                </button>

                <button
                  onClick={() => openEdit(item)}
                  className="text-xs px-3 py-1 rounded-full font-semibold bg-gray-600 hover:bg-gray-500 text-white"
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-start z-50 overflow-y-auto py-8">
          <div className="bg-gray-900 text-white rounded-lg p-6 w-full max-w-lg relative shadow-xl mx-4">
            <button
              onClick={() => setEditingItem(null)}
              className="absolute top-3 right-4 text-gray-400 hover:text-white text-2xl font-bold leading-none"
              aria-label="Close"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4">Edit Item</h2>

            {editSaved && (
              <p className="text-green-400 text-sm font-semibold mb-3">Saved!</p>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4">

              {/* Game */}
              <div>
                <label className="block text-sm font-semibold mb-1">Game</label>
                <select
                  name="games_id"
                  value={editForm.games_id}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">— Select a game —</option>
                  {gamesOptions.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold mb-1">Category</label>
                <select
                  name="types"
                  value={editForm.types}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">— Select a category —</option>
                  {typeOptions.map((t) => (
                    <option key={t.id} value={t.name}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Event */}
              <div>
                <label className="block text-sm font-semibold mb-1">Event</label>
                <select
                  name="event"
                  value={editForm.event}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">— Select an event —</option>
                  {eventOptions.map((e) => (
                    <option key={e.id} value={e.name}>{e.name}</option>
                  ))}
                </select>
              </div>

              {/* Maps Exclusive */}
              <div>
                <label className="block text-sm font-semibold mb-1">Stamp Type / Maps Exclusive</label>
                <select
                  name="maps"
                  value={editForm.maps}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">— Select —</option>
                  {mapsOptions.map((m) => (
                    <option key={m.id} value={m.name}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Maps Name */}
              <div>
                <label className="block text-sm font-semibold mb-1">Maps Name</label>
                <select
                  name="maps_name"
                  value={editForm.maps_name}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">— Select —</option>
                  {mapsNameOptions.map((mn) => (
                    <option key={mn.id} value={mn.name}>{mn.name}</option>
                  ))}
                </select>
              </div>

              {/* Acquisition Method */}
              <div>
                <label className="block text-sm font-semibold mb-1">Acquisition Method</label>
                <select
                  name="acquisition_method_id"
                  value={editForm.acquisition_method_id}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                >
                  <option value="">None / unspecified</option>
                  {acquisitionMethods.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Unlock Requirements */}
              {editUnlockOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold mb-1">Unlock Requirements</label>
                  <div className="space-y-1 max-h-36 overflow-y-auto border rounded p-2 bg-gray-800">
                    {editUnlockOptions.map((req) => (
                      <label key={req.id} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
                        <input
                          type="checkbox"
                          checked={editLinkedIds.has(req.id)}
                          onChange={() => toggleEditRequirement(req.id)}
                          className="w-4 h-4"
                        />
                        {req.name}{req.platform ? ` (${req.platform})` : ""}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* URL */}
              <div>
                <label className="block text-sm font-semibold mb-1">URL</label>
                <input
                  type="text"
                  name="url"
                  value={editForm.url}
                  onChange={handleEditChange}
                  className="p-2 w-full border rounded text-black"
                />
              </div>

              {/* Flags */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={editForm.is_active}
                    onChange={handleEditChange}
                    className="w-4 h-4"
                  />
                  Active (visible to players)
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_new"
                    checked={editForm.is_new}
                    onChange={handleEditChange}
                    className="w-4 h-4"
                  />
                  New this season
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    name="private_testing"
                    checked={editForm.private_testing}
                    onChange={handleEditChange}
                    className="w-4 h-4"
                  />
                  Private testing only
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-2">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded font-semibold"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-5 py-2 rounded font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditDelete}
                  className="bg-red-700 hover:bg-red-600 text-white px-5 py-2 rounded font-semibold"
                >
                  Delete
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageItems;
